import { TestAnswerFeedbackType } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileTypeEnum } from 'utils'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import {
  chainName,
  FieldColorPicker,
  FieldFile,
  FieldSegmented,
  FieldSelect,
  FormField,
} from 'components/formKit'
import Collapse from 'components/uiKit/Collapse'
import { Typography } from 'components/uiKit/Typography'
import { FULL_COLLAPSE_PADDING } from 'constants/styles'
import {
  useBrandColorOptions,
  useHiddenTestFeedback,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import type { TestHotspotValueFormType } from './testHotspotTypes'

const RESPONSE = genElementValueField({
  as: FieldSegmented<'single' | 'all'>,
  name: 'response',
  label: t('elements.testHotspot.form.response.label'),
  defaultValue: 'all',
  props: {
    options: [
      { value: 'single', label: t('elements.testHotspot.form.response.any') },
      { value: 'all', label: t('elements.testHotspot.form.response.all') },
    ],
  },
})

const IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'image',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
  },
})

const OVERLAY = genElementValueField({
  as: FieldColorPicker<string | null>,
  name: 'overlay',
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})

const TYPE = genElementValueField({
  as: FieldSelect<'icon' | 'withoutIcon'>,
  name: 'type',
  label: t('input.label.type'),
  defaultValue: 'icon',
  props: {
    fluid: true,
    options: [
      { value: 'icon', label: t('input.option.icons') },
      { value: 'withoutIcon', label: t('input.option.withoutIcon') },
    ],
  },
})

const ANSWER_FEEDBACK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'answerFeedback',
  label: t('elements.testAnswerFeedback.form.answerFeedback'),
  defaultValue: true,
})

const DISPLAY_FEEDBACK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'displayFeedback',
  label: t('elements.testAnswerFeedback.form.displayFeedback'),
  layout: 'horizontal',
  defaultValue: true,
})

const GENERAL_TYPE_FB = genElementValueField({
  as: FieldSelect<TestAnswerFeedbackType>,
  name: 'generalTypeFb',
  label: t('elements.testAnswerFeedback.form.generalTypeFb'),
  layout: 'horizontal',
  defaultValue: TestAnswerFeedbackType.validation,
  props: {
    options: [
      getEnumOption('TestFeedbackType', TestAnswerFeedbackType.validation),
      getEnumOption('TestFeedbackType', TestAnswerFeedbackType.info),
    ],
  },
  useConfigs: [useHiddenTestFeedback],
})

const TestHotspotForm: TestHotspotValueFormType = ({ name }) => {
  return (
    <>
      <Padding bottom gap sides>
        <FormField config={chainName(name, RESPONSE)} />
        <Typography styleType='hint'>{t('elements.testHotspot.form.hint')}</Typography>
        <FormField config={chainName(name, ANSWER_FEEDBACK)} />
        <FormField config={chainName(name, DISPLAY_FEEDBACK)} />
        <FormField config={chainName(name, GENERAL_TYPE_FB)} />
      </Padding>
      <Collapse
        header={t('elements.labeledGraphic.form.image')}
        iconPosition='right'
        name='image'
        paddings={FULL_COLLAPSE_PADDING}
        initialOpen
      >
        <Padding gap>
          <FormField config={chainName(name, IMAGE)} />
          <FormField config={chainName(name, OVERLAY)} />
        </Padding>
      </Collapse>
      <Collapse
        header={t('elements.testHotspot.tags.spot')}
        iconPosition='right'
        name='spot'
        paddings={FULL_COLLAPSE_PADDING}
        initialOpen
      >
        <Padding gap>
          <FormField config={chainName(name, TYPE)} />
        </Padding>
      </Collapse>
    </>
  )
}

export default TestHotspotForm
